import type React from "react";
import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ControllerRenderProps, useForm } from "react-hook-form";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ROUTES } from "@/enums/routes";
import { useDispatch, useSelector } from "react-redux";
import { setExpenses } from "@/store/redux/organization/expenseSlice";

import { expenseSchema } from "@/utils/schemaHelper";

import {
  createCategoryApi,
  createItemApi,
  getBranchApi,
  getCategoryApi,
  getExpensesApi,
  getItemApi,
  updateExpensesApi,
} from "@/modules/organizationServices";
import { setLoading } from "@/store/redux/appSlice";
import { RootState } from "@/store/store";
import { ChevronDown, X } from "lucide-react";

import { CategoryMessage, Expense, ItemSuccess } from "@/enums/errorMessages";

import { setBranches } from "@/store/redux/organization/branchSlice";
import { setCategories } from "@/store/redux/organization/categorySlice";
import { createExpenseFormData } from "@/utils/expenseHelper";
import { GetExpenseResponse } from "@/types/expense";
import { OrganizationType } from "@/types/apiResposne";
import { ErrorHelper } from "@/utils/errorHelper";
import {
  PaymentMethodsLabels,
  PaymentMethodsValues,
} from "@/enums/expenseFields";
import { toastMessage } from "@/utils/toastHelper";
import { ReusableButton } from "@/reusable/reusableButton";

import { BillPhotosInput } from "@/reusable/BillPhoto";
import { setItems } from "@/store/redux/organization/itemSlice";
import { TiTick } from "react-icons/ti";

type ExpenseFormValues = z.infer<typeof expenseSchema>;

const UpdateExpense = () => {
  const location = useLocation();
  const expense = location.state?.expense;

  const [searchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;
  const { id } = useParams<{ id: string }>();
  const expenseId = id ? parseInt(id, 10) : NaN;
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const categories = useSelector(
    (state: RootState) => state.category.categories,
  );
  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const items = useSelector((state: RootState) => state.item.items);
  const loading = useSelector((state: RootState) => state.app.loading);
  const branches = useSelector((state: RootState) => state.branch.branches);

  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail,
  );
  const orgId = userDetails?.organizationId;
  const navigate = useNavigate();
  // @ts-expect-error: skip next line
  const item = expense?.items.map((tag) => tag.id);
  const paymentOptions = [
    { value: PaymentMethodsValues.CASH, label: PaymentMethodsLabels.CASH },
    { value: PaymentMethodsValues.CHEQUE, label: PaymentMethodsLabels.CHEQUE },
    { value: PaymentMethodsValues.QR, label: PaymentMethodsLabels.ONLINE },
  ];
  const pic = expense?.billPhotos;

  const [selectedItem, setSelectedItem] = useState<number[]>(item ? item : []);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isCategoryOpen, setIsCategoryOpen] = useState<boolean>(false);
  const [createCategory, setCreateCategory] = useState<boolean>(false);
  const [searchText, setSearchText] = useState("");

  const [itemOpen, setItemOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filter = useSelector((state: RootState) => state.filter.filter);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (inputRef.current && !inputRef.current.contains(target)) {
        setItemOpen(false);
      }
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsCategoryOpen(false);
        setCreateCategory(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseSchema),
    mode: "onSubmit",
    defaultValues: {
      id: expenseId || 0,
      date: expense?.date || new Date().toISOString().split("T")[0],
      category: expense?.category || categories[0]?.id || 0,
      items: item || [],
      amount: expense?.amount ? Number(expense.amount) : 0,
      billPhotos: pic || [],
      reimbursedTo: expense.reimbursedTo,
      reimbursed: expense?.reimbursed || false,

      description: expense?.description || "",
      branch: expense?.branch || branches[0]?.id || 0,
      paymentMethod: expense?.paymentMethod || "cash",
    },
  });

  const getCategories = async () => {
    dispatch(setLoading(true));
    try {
      const res = await getCategoryApi();
      const response = res.categories;
      if (response != undefined) {
        const categories: OrganizationType.CategoryResponse[] = Array.isArray(
          response,
        )
          ? response
          : [response];
        dispatch(setCategories(categories));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const getExpenses = async (page: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getExpensesApi(page, filter);
      const fixedResponse: GetExpenseResponse = {
        count: response.count ?? 0,
        results: response.results ?? [],
        next: response.next ?? "",
        previous: response.previous ?? "",
      };
      if (response.count !== undefined) {
        dispatch(setExpenses(fixedResponse));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const getItems = async () => {
    try {
      const res = await getItemApi();
      const response = res.items;
      if (response != undefined) {
        const items = Array.isArray(response) ? response : [response];
        dispatch(setItems(items));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };
  const getBranches = async () => {
    dispatch(setLoading(true));
    try {
      if (!orgId) return;
      const res = await getBranchApi(orgId);
      const response = res.branches;
      if (response !== undefined) {
        const branches = Array.isArray(response) ? response : [response];
        dispatch(setBranches(branches));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    getCategories();
    getItems();

    getBranches();
  }, []);

  const createItem = async () => {
    try {
      const newItem = await createItemApi({
        name: searchText,
        organization: userDetails?.organizationId,
      });
      toastMessage("success", ItemSuccess.ITEM_CREATED);

      //@ts-expect-error: skip next line
      const updatedSelected = [...selectedItem, newItem.id];
      setSelectedItem(updatedSelected);
      form.setValue("items", updatedSelected);

      getItems();

      setSearchText("");
      setItemOpen(false);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const createCategoryHandler =
    (field: ControllerRenderProps<ExpenseFormValues, "category">) =>
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      setCreateCategory(false);
      const newId = await addCategory();

      if (newId !== null) {
        field.onChange(newId);
      }
    };

  const addCategory = async (): Promise<number | undefined> => {
    try {
      if (userDetails?.organizationId) {
        const newCategory = await createCategoryApi({
          name: newCategoryName,
          organization: userDetails?.organizationId,
        });
        toastMessage("success", CategoryMessage.CATEGORY_CREATED);
        await getCategories();
        //@ts-expect-error:skip
        const newCategoryId = newCategory.id;
        if (newCategoryId) {
          return newCategoryId;
        }
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const getSelectedItems = () => {
    return items.filter((item) => selectedItem.includes(item.id));
  };

  const onSubmit = async (values: ExpenseFormValues) => {
    if (loading) return;

    dispatch(setLoading(true));
    const formData = createExpenseFormData(values);

    try {
      await updateExpensesApi(expenseId, formData);
      toastMessage("success", Expense.EXPENSE_UPDATED);
      navigate(ROUTES.DASHBOARD);
      getExpenses(currentPage);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div className="   bg-white px-4">
      <div className=" w-2xl   bg-white p-8 rounded-lg ">
        <h2 className="text-center text-2xl font-semibold mb-6 text-gray-800">
          Edit Expense
        </h2>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
            noValidate
            encType="multipart/form-data"
          >
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      max={today.toISOString().split("T")[0]}
                      min={oneMonthAgo.toISOString().split("T")[0]}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl>
                    <div className="relative" ref={dropdownRef}>
                      {createCategory ? (
                        <div className="flex border p-2 rounded-md  items-center gap-2 ">
                          <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="New category name"
                            className="flex-grow outline-none"
                          />
                          <button
                            type="button"
                            onClick={createCategoryHandler(field)}
                          >
                            <TiTick size={24} className="text-green-500" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setCreateCategory(false);
                              setNewCategoryName("");
                            }}
                          >
                            <X className="text-red-500" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsCategoryOpen(!isCategoryOpen);
                            }}
                            className="w-full border p-2 rounded-md text-left flex items-center justify-between"
                          >
                            {field.value
                              ? categories.find((c) => c.id === field.value)
                                  ?.name
                              : "Select a category"}
                            <ChevronDown
                              size={16}
                              className={`transition-transform duration-200 ${
                                isCategoryOpen ? "rotate-180" : ""
                              }`}
                            />
                          </button>

                          {isCategoryOpen && (
                            <ul className="absolute left-0 w-full bg-white border rounded-md shadow z-50 max-h-60 overflow-y-auto">
                              {categories.map((category) => (
                                <li
                                  key={category.id}
                                  onClick={() => {
                                    field.onChange(category.id);
                                    setIsCategoryOpen(false);
                                  }}
                                  className={`p-2 hover:bg-gray-100 cursor-pointer ${
                                    field.value === category.id
                                      ? "bg-gray-300"
                                      : ""
                                  }`}
                                >
                                  {category.name}
                                </li>
                              ))}
                              <li
                                onClick={() => {
                                  setCreateCategory(true);
                                  setIsCategoryOpen(false);
                                }}
                                className="p-2 hover:bg-gray-100 cursor-pointer"
                              >
                                + Create New
                              </li>
                            </ul>
                          )}
                        </>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="items"
              render={() => (
                <FormItem>
                  <FormLabel>Items</FormLabel>
                  <FormControl>
                    <div className="relative w-full" ref={inputRef}>
                      <div className="border rounded-md p-2">
                        <div
                          className="flex flex-wrap items-center gap-1  cursor-text"
                          onClick={() => inputRef.current?.focus()}
                        >
                          {getSelectedItems().map((item) => (
                            <span
                              key={item.id}
                              className="bg-gray-100 px-2 py-1 rounded-full text-sm flex items-center gap-1"
                            >
                              {item.name}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSelectedItem((prev) =>
                                    prev.filter((id) => id !== item.id),
                                  );
                                }}
                                className="text-black cursor-pointer"
                              >
                                ×
                              </button>
                            </span>
                          ))}

                          <input
                            type="text"
                            className="flex-grow min-w-[100px] outline-none bg-transparent"
                            placeholder={
                              selectedItem.length == 0 ? "Search Items..." : ""
                            }
                            value={searchText}
                            onChange={(e) => {
                              setSearchText(e.target.value);
                              setItemOpen(true);
                            }}
                            onFocus={() => setItemOpen(true)}
                          />
                        </div>

                        {itemOpen && (
                          <div className="absolute z-10 top-full left-0 right-0 mt-0 bg-white border rounded-md max-h-60 overflow-y-auto shadow-lg">
                            {items
                              .filter(
                                (item) =>
                                  item.name
                                    .toLowerCase()
                                    .includes(searchText.toLowerCase()) &&
                                  !selectedItem.includes(item.id),
                              )
                              .map((tag) => (
                                <div
                                  key={tag.id}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const updatedItems = [
                                      ...selectedItem,
                                      tag.id,
                                    ];
                                    setSelectedItem(updatedItems);
                                    form.setValue("items", updatedItems);
                                    setSearchText("");
                                    setItemOpen(false);
                                  }}
                                  className="p-2 cursor-pointer hover:bg-gray-100"
                                >
                                  {tag.name}
                                </div>
                              ))}
                            {items.filter(
                              (item) =>
                                item.name.toLowerCase() ===
                                searchText.toLowerCase(),
                            ).length === 0 && (
                              <div
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  createItem();
                                }}
                                className="p-2 text-sm text-gray-500 cursor-pointer hover:bg-gray-100"
                              >
                                Create Item
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="reimbursed"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reimbursed</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        ref={field.ref}
                      />
                      <span>Reimbursed</span>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="reimbursedTo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reimbursed To</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <input
                        className="border pl-3 w-full h-10 rounded-md pl-1 "
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount (NPR.)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]"
                      type="number"
                      inputMode="decimal"
                      value={field.value || ""} // Ensure value is never undefined
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          field.onChange(
                            value === "" ? null : parseFloat(value),
                          );
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="billPhotos"
              render={() => (
                <FormItem>
                  <FormLabel>Receipt Images</FormLabel>
                  <FormControl>
                    <BillPhotosInput
                      name="billPhotos"
                      control={form.control}
                      maxSizeMB={8}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <textarea
                      rows={4}
                      {...field}
                      className="border pl-3 pt-2 pr-3 rounded-md"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="branch"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Branch</FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap gap-10">
                      {branches.map((branch) => (
                        <label
                          key={branch.id}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <input
                            type="radio"
                            value={branch.id}
                            checked={field.value === branch.id}
                            onChange={() => field.onChange(branch.id)}
                            className="form-radio cursor-pointer text-blue-600"
                          />
                          <span>{branch.branchName}</span>
                        </label>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="paymentMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Method</FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap gap-10">
                      {paymentOptions.map((payment) => (
                        <label
                          key={payment.value}
                          className="flex cursor-pointer items-center gap-2"
                        >
                          <input
                            type="radio"
                            value={payment.value}
                            checked={field.value === payment.value}
                            onChange={() => field.onChange(payment.value)}
                            className="form-radio cursor-pointer text-blue-600"
                          />
                          <span>{payment.label}</span>
                        </label>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-center ">
              <ReusableButton
                className="w-full"
                label="Update"
                loading={loading}
              />
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default UpdateExpense;
